import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, CheckCircle, TrendingUp, Users, Zap, Globe, BarChart3, Target, Briefcase, Search, Database, Star, MessageSquare } from 'lucide-react';
import { Header } from '@/layouts/Header';
import { Footer } from '@/layouts/Footer';
import { StickyCTA } from '@/components/StickyCTA';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from "@/features/shared/SEOHead";
import { trackCTAClick, trackNavClick } from '@/lib/utils';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { generateLocalBusinessSchema } from '@/lib/seo-schema';

const services = [
  {
    title: "CRM Implementation",
    description: "Custom CRM systems for Toronto's professional services, tech startups, and B2B teams. GoHighLevel, HubSpot, and ActiveCampaign expertise.",
    result: "Result: every GTA lead tracked, nurtured, and closed in one place.",
    icon: Users,
    link: "/solutions/crm-automation/"
  },
  {
    title: "PPC & Google Ads",
    description: "ROI-focused Google, Meta, and LinkedIn campaigns built for the GTA's most competitive niches: law, trades, healthcare, real estate, and SaaS.",
    result: "Result: lower cost per lead with full ad-spend-to-revenue visibility.",
    icon: Target,
    link: "/solutions/lead-generation-system/"
  },
  {
    title: "Web Design & Development",
    description: "Modern, mobile-first websites for Toronto businesses, with integrated CRM, analytics, and conversion tracking baked in from day one.",
    result: "Result: a site that converts GTA traffic instead of just looking good.",
    icon: Globe,
    link: "/solutions/websites/"
  },
  {
    title: "Marketing Automation",
    description: "Zapier, Make.com, and custom API integrations for fast-moving Toronto teams. Automate lead follow-up, data sync, and customer workflows.",
    result: "Result: instant speed-to-lead while your competitors sleep.",
    icon: Zap,
    link: "/solutions/crm-automation/"
  },
  {
    title: "Local SEO",
    description: "Rank higher across Toronto and the GTA. Google Business Profile optimization, local link building, and technical SEO for Canadian businesses.",
    result: "Result: compounding organic visibility in GTA search.",
    icon: Search,
    link: "/solutions/local-seo-reputation/"
  },
  {
    title: "Custom AI Solutions",
    description: "AI-powered automation for Toronto enterprises: custom chatbots, voice AI, and intelligent workflows that scale your operations without scaling headcount.",
    result: "Result: manual grunt work replaced with reliable automation.",
    icon: Briefcase,
    link: "/solutions/ai-strategy-rollout/"
  }
];

const industries = [
  { name: "Professional Services", href: "/solutions/" },
  { name: "Tech & SaaS Startups", href: "/solutions/ai-strategy-rollout/" },
  { name: "Real Estate & Development", href: "/solutions/" },
  { name: "Financial Services", href: "/solutions/crm-automation/" },
  { name: "Healthcare & Wellness", href: "/solutions/" },
  { name: "Legal & Consulting", href: "/solutions/" },
  { name: "Automotive Groups", href: "/automotive/" },
  { name: "Trades & Contractors", href: "/industries/trades/" },
];

const torontoInsights = [
  { stat: "6.2M+", label: "GTA Population" },
  { stat: "340K+", label: "Small & Medium Businesses" },
  { stat: "$400B+", label: "Annual GDP" },
  { stat: "2,000+", label: "Tech Startups" },
];

const faqs = [
  {
    q: "Do you serve Toronto and the Greater Toronto Area?",
    a: "Yes. While Frayze is headquartered in Thunder Bay, Ontario, we actively serve businesses across Toronto, Mississauga, Brampton, Markham, Vaughan, Richmond Hill, and the entire GTA. Our remote-first model lets us deliver Toronto-caliber digital systems without downtown overhead. We run discovery sessions, strategy meetings, and training over Zoom, and give you 24/7 access to your dashboards and analytics. Distance doesn't limit results. We've helped clients across Ontario and into the United States achieve measurable growth."
  },
  {
    q: "Why should a Toronto business work with a Thunder Bay agency?",
    a: "Toronto agencies often charge premium rates to cover high overhead: downtown office rent, GTA salaries, and operating costs. Frayze delivers the same services (CRM implementation, PPC management, web design, and marketing automation) at roughly 30–40% lower cost, without sacrificing expertise or results. Our remote-first approach means faster turnarounds, flexible communication, and a dedicated team that isn't juggling back-to-back GTA client meetings."
  },
  {
    q: "How much do your services cost for Toronto businesses?",
    a: "The 7-Day Website Launch is the entry point: a $997 build plus $189/month base, with communication usage billed like a phone plan. Larger managed systems (CRM, PPC, web design, and automation) are scoped to your business on a quick call, and you get a fixed quote before you commit. Pricing is transparent and upfront, with no hidden fees and no surprise invoices."
  },
  {
    q: "What makes you different from other Toronto digital marketing agencies?",
    a: "Integrated systems, not scattered tools. Most Toronto agencies offer siloed services: just SEO, just ads, or just web design. Frayze builds complete growth systems where your CRM, website, advertising, and automation all work together in one dashboard. Every system we build includes intelligent, AI-assisted workflows, and we track real metrics (leads, conversions, and revenue) with no long-term contracts you can't escape."
  },
  {
    q: "Can you run Google Ads and PPC campaigns targeting Toronto customers?",
    a: "Absolutely. We specialize in hyper-local Google Ads campaigns that target specific Toronto neighbourhoods, GTA suburbs, or demographic segments (from a Yorkville boutique targeting high-income shoppers to a Scarborough contractor targeting specific postal codes). Our PPC services span Google (Search, Display, YouTube), Meta, and LinkedIn for B2B, and every platform integrates with your CRM so each lead is tracked, nurtured, and attributed back to ad spend."
  },
  {
    q: "What's your track record with competitive, high-volume markets?",
    a: "Before Frayze, our founder built and ran the CRM, AI, and lead-generation system for a six-rooftop GTA automotive group: delivering a 400% increase in leads and $1.7M in annual savings. Frayze is built on those same systems. We understand the GTA's market dynamics: high competition, sophisticated buyers, and the need for enterprise-grade tools at SMB budgets."
  },
  {
    q: "How quickly can you get a Toronto business up and running?",
    a: "Quick-Start tools deploy in 2–3 days. Custom website projects with CRM integration take 2–4 weeks. A full CRM implementation with automation and team training runs 3–6 weeks, and a complete growth system (CRM + website + ads + automation) typically takes 6–8 weeks."
  }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((item) => ({
    "@type": "Question",
    "name": item.q,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.a
    }
  }))
};

const localBusinessSchema = generateLocalBusinessSchema({
  name: "Frayze Technologies Inc.: Toronto Digital Marketing & AI Automation",
  description: "Digital marketing and AI automation agency serving Toronto and the GTA with CRM implementation, PPC management, web design, SEO, and marketing automation.",
  url: "https://frayze.ca/service-areas/toronto/",
  areaServed: { name: "Toronto", containedIn: "Ontario" },
  serviceType: [
    "Digital Marketing",
    "CRM Implementation",
    "PPC Management",
    "Web Design",
    "SEO Services",
    "Marketing Automation",
    "AI Automation"
  ]
});

const Toronto = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SEOHead
        title="AI Automation & CRM Systems Toronto | Frayze"
        description="Capture more GTA leads and scale faster with one integrated CRM, automation, and AI system from Frayze. Toronto-caliber results at lower cost. Book a free consultation."
        keywords="crm implementation toronto, ai automation toronto, marketing automation agency toronto, ppc management toronto, web design toronto, local seo toronto, business growth systems gta"
        ogTitle="AI Automation & CRM Systems Toronto | Frayze"
        ogDescription="Capture more GTA leads and scale faster with one integrated CRM, automation, and AI system from Frayze. Toronto-caliber results at lower cost."
        twitterTitle="AI Automation & CRM Systems Toronto | Frayze"
        twitterDescription="Scale your Toronto business with integrated CRM, marketing automation, and AI systems."
        canonical="https://frayze.ca/service-areas/toronto/"
        robots="index, follow, max-snippet:-1, max-image-preview:large"
        breadcrumbs={[
          { name: "Home", item: "https://frayze.ca/" },
          { name: "Service Areas", item: "https://frayze.ca/service-areas/" },
          { name: "Toronto", item: "https://frayze.ca/service-areas/toronto/" }
        ]}
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [localBusinessSchema, faqSchema]
        }}
      />

      {/* Breadcrumb */}
      <div className="container mx-auto px-6 py-4 absolute top-20 z-20">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/service-areas/">Service Areas</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Toronto</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <main>
        {/* ===== HERO SECTION ===== */}
        <section className="relative overflow-hidden pt-40 md:pt-48 pb-20">
          <div
            className="absolute top-0 left-0 w-full h-[800px] pointer-events-none overflow-hidden z-0"
            style={{
              maskImage: 'radial-gradient(ellipse 72% 58% at 50% 40%, black 0%, black 25%, transparent 70%)',
              WebkitMaskImage: 'radial-gradient(ellipse 72% 58% at 50% 40%, black 0%, black 25%, transparent 70%)'
            }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(0,102,255,0.35),transparent_55%),radial-gradient(circle_at_82%_12%,rgba(127,0,255,0.28),transparent_60%),radial-gradient(circle_at_50%_82%,rgba(0,204,255,0.18),transparent_65%)] blur-3xl" />
          </div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-6 border-primary/20 bg-primary/5 text-primary backdrop-blur-sm">
                <MapPin className="h-3 w-3 mr-1.5" />
                Serving Toronto & the Greater Toronto Area
              </Badge>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
                AI Automation & CRM Systems{" "}
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  for Toronto Businesses
                </span>
              </h1>

              <p className="text-2xl md:text-3xl font-bold mb-4">
                Toronto-Caliber Performance. Northern Ontario Pricing.
              </p>

              <p className="text-xl md:text-2xl text-muted-foreground mb-10 leading-relaxed max-w-3xl mx-auto">
                CRM, PPC, websites, automation, and AI: one integrated growth system
                engineered for the GTA's most competitive industries, run remotely from our
                Thunder Bay HQ.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mb-8">
                <Button
                  size="lg"
                  className="h-14 px-8 text-lg font-bold shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all duration-300 group"
                  asChild
                >
                  <a
                    href="/book-consultation/"
                    onClick={() => trackCTAClick({ cta_text: 'Book Free Toronto Consultation', cta_location: 'toronto_hero', cta_type: 'consultation_cta', cta_url: '/book-consultation/' })}
                  >
                    Book My Free Consultation
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="h-14 px-8 text-lg hover:bg-muted/50 bg-background/50 backdrop-blur-sm"
                  asChild
                >
                  <a
                    href="/digital-audit/"
                    onClick={() => trackNavClick({ nav_text: 'Get Your Automation Score', nav_location: 'toronto_hero', nav_type: 'audit_navigation', nav_url: '/digital-audit/' })}
                  >
                    Get Your Automation Score
                  </a>
                </Button>
              </div>

              <div className="flex flex-wrap gap-4 justify-center text-sm text-muted-foreground">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2 text-primary" />
                  <span>Remote-First, Results-Driven</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2 text-primary" />
                  <span>Lower Cost Than GTA Agencies</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2 text-primary" />
                  <span>No Long-Term Contracts</span>
                </div>
              </div>
            </div>

            {/* ===== PROOF STRIP ===== */}
            <div className="mt-16 mx-auto max-w-5xl">
              <div className="rounded-2xl border border-border/60 bg-muted/40 backdrop-blur-sm px-6 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="text-center md:text-left">
                    <div className="text-primary font-bold text-sm mb-1 uppercase tracking-wider">Founder Track Record</div>
                    <div className="text-xl font-bold">Built on a 6-Rooftop GTA Dealer Group</div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                    <div>
                      <div className="text-3xl font-bold text-primary">47%</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Sales Volume Growth</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-primary">52%</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Lower Tech Costs</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-primary">400%</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Lead Increase</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-primary">$1.7M</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Saved Annually</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== WHY GTA BUSINESSES NEED A GROWTH SYSTEM ===== */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Toronto Businesses Need a Connected Growth System</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                The GTA market is fast, competitive, and unforgiving. You need systems that deliver
                measurable revenue, not just "marketing." Most businesses are still running on
                disconnected tools, and that costs them leads, time, and money.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="border-red-200 bg-red-50/50">
                <CardContent className="pt-6">
                  <div className="text-red-600 font-bold mb-2">The Problem</div>
                  <p className="text-sm text-muted-foreground">
                    Disconnected tools, manual follow-up, leads falling through the cracks, and no clear view of what's actually working.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-yellow-200 bg-yellow-50/50">
                <CardContent className="pt-6">
                  <div className="text-yellow-700 font-bold mb-2">The Cost</div>
                  <p className="text-sm text-muted-foreground">
                    The average business loses 30–50% of its leads to slow follow-up. In the GTA's competitive market, that's thousands in lost revenue every month.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50/50">
                <CardContent className="pt-6">
                  <div className="text-green-700 font-bold mb-2">The Solution</div>
                  <p className="text-sm text-muted-foreground">
                    A unified growth system from Frayze that captures, nurtures, and converts leads automatically, with one dashboard to manage it all.
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-gradient-to-br from-primary to-accent text-white border-0">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6">What you get with Frayze:</h3>
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
                  {[
                    "Toronto-level CRM implementations without Toronto overhead",
                    "High-performance PPC for competitive niches (law, trades, healthcare, real estate, SaaS)",
                    "Powerful automation delivered in weeks, not months",
                    "A full-stack team without big-city agency overhead",
                    "Transparent pricing, full analytics, and no long-term contracts",
                    "A remote-first partner that executes faster and communicates clearer",
                  ].map((item) => (
                    <div key={item} className="flex items-start">
                      <CheckCircle className="h-5 w-5 mr-3 mt-0.5 text-white flex-shrink-0" />
                      <span className="text-white/95">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ===== MARKET INSIGHTS ===== */}
        <section className="py-20 bg-muted/30 border-y">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {torontoInsights.map((insight) => (
                <div key={insight.label}>
                  <div className="text-4xl font-bold text-primary mb-1">{insight.stat}</div>
                  <div className="text-sm text-muted-foreground">{insight.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SERVICES ===== */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Digital Marketing & Automation Services for
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Toronto Businesses</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                From CRM implementation to Google Ads, we deliver the complete digital growth stack
                Toronto businesses need to scale in Canada's most competitive market.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => {
                const IconComponent = service.icon;
                return (
                  <Card key={service.title} className="group hover:shadow-md transition-shadow border-primary/5">
                    <CardHeader>
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors text-primary">
                        <IconComponent className="h-6 w-6" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                      <CardDescription>{service.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm font-semibold mb-4">{service.result}</p>
                      <Button variant="link" className="p-0 h-auto text-primary" asChild>
                        <Link
                          to={service.link}
                          onClick={() => trackNavClick({ nav_text: `Learn More - ${service.title}`, nav_location: 'toronto_services', nav_type: 'service_navigation', nav_url: service.link })}
                        >
                          Learn More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* ===== INDUSTRIES WE SERVE ===== */}
        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Toronto Industries We Serve</h2>
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
              From Bay Street to Bloor West, we help GTA businesses across diverse sectors scale their
              digital operations and drive measurable growth.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {industries.map((industry) => (
                <Link
                  key={industry.name}
                  to={industry.href}
                  className="flex items-center gap-2 p-4 rounded-xl border bg-background hover:border-primary/30 hover:bg-primary/5 transition-all group"
                >
                  <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm font-medium group-hover:text-primary transition-colors">{industry.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ===== HOW WE WORK: PROCESS ===== */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">How We Build Your Growth System</h2>
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
              Every Toronto engagement follows our proven 4-step process. We start with data, build with
              precision, and deliver measurable results.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Discovery & Audit", body: "We map your current tools, identify bottlenecks, and analyze your competitive position in the GTA market." },
                { step: "02", title: "Design & Build", body: "We design your custom growth system (website, CRM, automation, and campaigns) in a staging environment before going live." },
                { step: "03", title: "Launch & Optimize", body: "Go-live with a full optimization blitz. We monitor performance, run A/B tests, and refine until your system hits peak efficiency." },
                { step: "04", title: "Support & Scale", body: "Ongoing support, updates, and data-driven improvements. As you grow, your system grows with you." },
              ].map((item) => (
                <div key={item.step} className="space-y-3 text-center">
                  <div className="text-5xl font-bold text-primary/70">{item.step}</div>
                  <div className="font-bold text-lg">{item.title}</div>
                  <p className="text-sm text-muted-foreground">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== CORE GROWTH SYSTEMS: INTERNAL LINKING HUB ===== */}
        <section className="py-20 bg-muted/30 border-y">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-3">Our Core Growth Systems</h2>
              <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
                Every Toronto engagement is powered by our proven systems. Each module works independently but they're designed to work together.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link to="/solutions/websites/" className="group p-6 bg-background rounded-2xl border hover:border-primary/30 hover:shadow-lg transition-all">
                <Globe className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Web Design & Local SEO</h3>
                <p className="text-sm text-muted-foreground">High-performance websites built for search visibility and lead conversion across Toronto and the GTA.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">Explore system <ArrowRight className="w-3 h-3" /></span>
              </Link>
              <Link to="/solutions/crm-automation/" className="group p-6 bg-background rounded-2xl border hover:border-primary/30 hover:shadow-lg transition-all">
                <Users className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">CRM Automation</h3>
                <p className="text-sm text-muted-foreground">Centralized lead management with automated follow-up, pipeline tracking, and review generation.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">Explore system <ArrowRight className="w-3 h-3" /></span>
              </Link>
              <Link to="/solutions/lead-generation-system/" className="group p-6 bg-background rounded-2xl border hover:border-primary/30 hover:shadow-lg transition-all">
                <Target className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Lead Generation</h3>
                <p className="text-sm text-muted-foreground">Multi-channel lead capture and AI-powered instant follow-up that fills your calendar with qualified GTA customers.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">Explore system <ArrowRight className="w-3 h-3" /></span>
              </Link>
              <Link to="/solutions/ai-strategy-rollout/" className="group p-6 bg-background rounded-2xl border hover:border-primary/30 hover:shadow-lg transition-all">
                <Zap className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Enterprise AI</h3>
                <p className="text-sm text-muted-foreground">Voice AI, automated workflows, and custom automation that replaces manual grunt work across your operation.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">Explore system <ArrowRight className="w-3 h-3" /></span>
              </Link>
              <Link to="/solutions/ad-management-scale/" className="group p-6 bg-background rounded-2xl border hover:border-primary/30 hover:shadow-lg transition-all">
                <TrendingUp className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Ad Management & Scale</h3>
                <p className="text-sm text-muted-foreground">Google & Meta ads with full ROI visibility, optimized for competitive GTA industries.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">Explore system <ArrowRight className="w-3 h-3" /></span>
              </Link>
              <Link to="/case-studies/" className="group p-6 bg-primary/5 rounded-2xl border border-primary/20 hover:border-primary/40 hover:shadow-lg transition-all">
                <Briefcase className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">See the Proof</h3>
                <p className="text-sm text-muted-foreground">Real systems, real results. Explore the case studies behind our growth approach.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">View case studies <ArrowRight className="w-3 h-3" /></span>
              </Link>
            </div>
          </div>
        </section>

        {/* ===== CASE STUDY PROOF ===== */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-3">Built on Systems Proven in the GTA</h2>
              <p className="text-muted-foreground text-lg">The track record behind Frayze: built in one of Canada's most competitive markets.</p>
            </div>
            <Link to="/case-studies/need-a-car-automotive-growth/" className="group flex flex-col md:flex-row items-center gap-8 bg-muted/30 border rounded-2xl p-8 md:p-12 hover:border-primary/40 hover:shadow-lg transition-all">
              <div className="flex-1">
                <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Founder Track Record • Automotive</Badge>
                <h3 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">Need A Car: A Founder-Built GTA Growth System</h3>
                <p className="text-muted-foreground text-lg mb-4">Before Frayze, our founder built and ran the CRM, AI, and lead-generation system that delivered a 400% lead increase and $1.7M in annual marketing savings for a six-location GTA automotive group. Frayze is those same systems, now available to your Toronto business.</p>
                <span className="text-primary font-bold inline-flex items-center gap-1">Read the full case study <ArrowRight className="w-4 h-4" /></span>
              </div>
              <div className="w-full md:w-48 shrink-0">
                <div className="bg-primary/5 rounded-2xl p-6 text-center">
                  <div className="text-5xl font-bold text-primary">400%</div>
                  <div className="text-sm font-medium text-muted-foreground mt-1">Lead Increase</div>
                  <div className="text-xs text-muted-foreground mt-1">+ $1.7M saved</div>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* ===== RELATED SERVICE AREAS ===== */}
        <section className="py-20 bg-muted/30 border-y">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Serving Businesses Across Ontario</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Remote-first growth systems that help businesses in these markets get found and capture more local leads.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <Card className="hover:shadow-lg transition-all border-2 hover:border-primary/30">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">Thunder Bay, Ontario</h3>
                  <p className="text-sm text-muted-foreground mb-3">Our HQ: local digital solutions for Northwestern Ontario businesses.</p>
                  <Button variant="ghost" size="sm" className="p-0 h-auto text-primary" asChild>
                    <Link to="/service-areas/thunder-bay/">Learn More <ArrowRight className="h-4 w-4 ml-1 inline" /></Link>
                  </Button>
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-all border-2 hover:border-primary/30">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">Sudbury, Ontario</h3>
                  <p className="text-sm text-muted-foreground mb-3">Northern Ontario digital marketing and automation expertise.</p>
                  <Button variant="ghost" size="sm" className="p-0 h-auto text-primary" asChild>
                    <Link to="/service-areas/sudbury/">Learn More <ArrowRight className="h-4 w-4 ml-1 inline" /></Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* ===== FAQ SECTION ===== */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6 max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-muted-foreground">Working with a Thunder Bay agency for Toronto services.</p>
            </div>
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((item, index) => (
                <AccordionItem key={index} value={`item-${index + 1}`} className="border rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-2xl border border-primary/20 bg-primary/5 text-center p-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Scale Your Toronto Business?</h2>
                <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
                  Book a free consultation and we'll audit your current systems, identify growth
                  opportunities, and show you exactly how Frayze can help you win in the GTA. No fluff.
                  just a clear, practical plan.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    className="h-14 px-10 text-lg font-bold shadow-lg shadow-primary/25"
                    asChild
                  >
                    <a
                      href="/book-consultation/"
                      onClick={() => trackCTAClick({ cta_text: 'Book Your Free Toronto Consultation', cta_location: 'toronto_final_cta', cta_type: 'consultation_cta', cta_url: '/book-consultation/' })}
                    >
                      Book My Free Consultation
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-14 px-10 text-lg font-bold"
                    asChild
                  >
                    <a href="tel:+18077000079">Call (807) 700-0079</a>
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground mt-6">
                  Serving Toronto, Mississauga, Brampton, Markham, Vaughan, Richmond Hill, and the entire GTA.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyCTA />
    </div>
  );
};

export default Toronto;
